/**
 * `window.__GAME__` — o contrato que o E2E interroga em vez de tirar screenshot
 * de canvas (REV-13). É CONTRATO: tem `version`, e quebrar o formato quebra o
 * teste de todo mundo. Mudou o formato, sobe a versão.
 *
 * **Só leitura, de propósito** (ADR-0009). Nada aqui manda intenção, força
 * reconexão ou mexe no jogo: um caminho que injeta intenção sem passar pelo
 * controle é trapaça pronta se a flag vazar ligada, e o E2E dirige melhor pelo
 * teclado real, que é o caminho do jogador.
 *
 * O que ele expõe sai do que `Connection`, `World` e `FrameSampler` JÁ sabem.
 * NEVER crie estado só para depurar: o que o teste observa tem que ser o que o
 * jogador vive.
 */
import { PROTOCOL_VERSION } from '@game/protocol';
import { isEnabled } from './flags.js';

/** ALWAYS incremente ao mudar o formato de `DebugSnapshot`. */
export const DEBUG_API_VERSION = 3;
const GLOBAL_KEY = '__GAME__';

/**
 * `solo` é estado próprio, e NEVER um `connecting` que nunca termina: partida
 * local não tem socket para abrir, e um contrato que diz "conectando" para sempre
 * é o contrato mentindo para quem o lê.
 */
export type DebugNetStatus = 'connecting' | 'open' | 'closed' | 'solo';

export interface DebugPlayer {
  readonly id: number;
  readonly x: number;
  readonly z: number;
  readonly hp: number;
}

export interface DebugNet {
  readonly status: DebugNetStatus;
  readonly matchId: string | undefined;
  readonly playerId: number | undefined;
  readonly tick: number;
  readonly lastAckSeq: number;
  readonly rttMs: number | undefined;
  readonly closeReason: string | undefined;
}

/**
 * Um corpo da sala. Existe porque a fatia do ímã precisa ser observável pelo
 * E2E — e o E2E asserta sobre ESTE contrato, NEVER sobre pixel de canvas 3D.
 */
export interface DebugBody {
  readonly id: number;
  readonly kind: string;
  readonly x: number;
  readonly z: number;
  readonly heldBy: number;
}

export interface DebugPerf {
  readonly frames: number;
  readonly p95Ms: number;
  readonly worst1PercentMs: number;
  readonly maxMs: number;
}

/**
 * O que sai no fio para o Playwright. Tudo serializável: NEVER `undefined` no
 * meio, porque `JSON.stringify` some com a chave e o teste passa a comparar com
 * um buraco em vez de com um valor ausente.
 */
export interface DebugSnapshot {
  readonly version: number;
  readonly protocolVersion: number;
  /**
   * Qual build produziu este retrato. Entra no contrato porque snapshot sem
   * versão NEVER é prova: um E2E que falha, ou um relato de bug colado no chat,
   * valem pouco quando não se sabe qual código os gerou.
   */
  readonly build: { readonly sha: string; readonly time: string; readonly env: string };
  readonly net: {
    readonly status: DebugNetStatus;
    readonly matchId: string | null;
    readonly playerId: number | null;
    readonly tick: number;
    readonly lastAckSeq: number;
    readonly rttMs: number | null;
    readonly closeReason: string | null;
  };
  readonly players: readonly DebugPlayer[];
  readonly bodies: readonly DebugBody[];
  /** Corpo magnetizado pelo jogador local; 0 = campo desligado. */
  readonly magnetTargetId: number;
  readonly perf: DebugPerf;
  readonly flags: Readonly<Record<string, boolean>>;
}

/** Leitores injetados pelo `main.ts`. Mantém este arquivo sem saber de socket nem de three. */
export interface DebugSources {
  readonly build: () => { readonly sha: string; readonly time: string; readonly env: string };
  readonly net: () => DebugNet;
  readonly players: () => readonly DebugPlayer[];
  /** Sala vazia em partida em rede: o protocolo NEVER carrega corpo (ainda). */
  readonly bodies: () => readonly DebugBody[];
  readonly magnetTargetId: () => number;
  readonly perf: () => DebugPerf;
  readonly flags: () => Readonly<Record<string, boolean>>;
}

export interface DebugApi {
  readonly version: number;
  readonly snapshot: () => DebugSnapshot;
}

function orNull<T>(value: T | undefined): T | null {
  return value ?? null;
}

/**
 * Monta o retrato. **Copia tudo**: `FrameSampler.compute()` reaproveita o objeto
 * de retorno e a lista de jogadores é a do último snapshot da rede — devolver a
 * referência deixaria o teste segurando estado vivo que muda debaixo dele.
 */
export function buildSnapshot(sources: DebugSources): DebugSnapshot {
  const net = sources.net();
  const perf = sources.perf();
  const build = sources.build();
  return {
    version: DEBUG_API_VERSION,
    protocolVersion: PROTOCOL_VERSION,
    build: { sha: build.sha, time: build.time, env: build.env },
    net: {
      status: net.status,
      matchId: orNull(net.matchId),
      playerId: orNull(net.playerId),
      tick: net.tick,
      lastAckSeq: net.lastAckSeq,
      rttMs: orNull(net.rttMs),
      closeReason: orNull(net.closeReason),
    },
    players: sources.players().map((player) => ({
      id: player.id,
      x: player.x,
      z: player.z,
      hp: player.hp,
    })),
    bodies: sources.bodies().map((body) => ({
      id: body.id,
      kind: body.kind,
      x: body.x,
      z: body.z,
      heldBy: body.heldBy,
    })),
    magnetTargetId: sources.magnetTargetId(),
    perf: {
      frames: perf.frames,
      p95Ms: perf.p95Ms,
      worst1PercentMs: perf.worst1PercentMs,
      maxMs: perf.maxMs,
    },
    flags: { ...sources.flags() },
  };
}

/**
 * Instala em `target` (o `globalThis`, ou um objeto de mentira no teste).
 * Devolve como desinstalar. Com a flag desligada NEVER escreve nada — nem uma
 * chave vazia, que já seria pista de que existe algo para procurar.
 */
export function installDebugApi(
  sources: DebugSources,
  target: Record<string, unknown> = globalThis,
): () => void {
  if (!isEnabled('debugApi')) return () => undefined;

  // Congelado: acrescentar comando em tempo de execução NEVER pode ser possível.
  // A decisão de v1 ser só leitura (ADR-0009) fica valendo como propriedade do
  // objeto, e não como boa intenção.
  const api: DebugApi = Object.freeze({
    version: DEBUG_API_VERSION,
    snapshot: () => buildSnapshot(sources),
  });

  target[GLOBAL_KEY] = api;
  return () => {
    delete target[GLOBAL_KEY];
  };
}
